import React, { useState, useEffect } from 'react';
import Lock from 'lucide-react/dist/esm/icons/lock';
import Mail from 'lucide-react/dist/esm/icons/mail';
import Sparkles from 'lucide-react/dist/esm/icons/sparkles';
import Eye from 'lucide-react/dist/esm/icons/eye';
import EyeOff from 'lucide-react/dist/esm/icons/eye-off';
import { login } from '../services/auth';
import { getTranslation } from '../../../i18n';

const LoginForm = ({ onLoginSuccess, onShowForgotPassword, onShowNewPassword }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [language] = useState('en');
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [capsLockOn, setCapsLockOn] = useState(false);

  const t = (key) => getTranslation(language, key);

  useEffect(() => {
    const savedEmail = localStorage.getItem('rememberedEmail');
    if (savedEmail) {
      setEmail(savedEmail);
      setRememberMe(true);
    }
  }, []);

  const handleKeyPress = (e) => {
    const isCapsLock = e.getModifierState && e.getModifierState('CapsLock');
    setCapsLockOn(isCapsLock);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(email, password);

    if (result.success) {
      if (rememberMe) {
        localStorage.setItem('rememberedEmail', email);
      } else {
        localStorage.removeItem('rememberedEmail');
      }
      onLoginSuccess();
    } else if (result.challenge === 'NEW_PASSWORD_REQUIRED') {
      onShowNewPassword(email, result.session);
    } else {
      setError(result.error || t('loginFailed'));
    }

    setLoading(false);
  };

  return (
    <div className="relative h-full w-full flex justify-center items-center p-8">
      {/* Background glow effect */}
      <div 
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full pointer-events-none animate-pulse"
        style={{ background: 'radial-gradient(circle, rgba(33, 158, 188, 0.15) 0%, transparent 70%)' }}
      />

      {/* Login Box */}
      <div className="relative z-10 w-full max-w-[450px] px-10 py-7 rounded-2xl border border-accent bg-glass-strong backdrop-blur-2xl shadow-xl-dark animate-fade-in">
        {/* Header */}
        <div className="flex flex-col items-center text-center mb-7">
          <div className="relative mb-5">
            <img 
              src="/quazi-logo.svg" 
              alt="QUAZI Logo" 
              className="w-[72px] h-auto drop-shadow-[0_0_20px_rgba(33,158,188,0.7)]" 
            />
            <Sparkles 
              className="absolute -top-2 -right-2 text-brand-light animate-pulse" 
              size={24} 
            />
          </div>
          <h1 className="font-display text-5xl font-bold tracking-[0.15em] mb-2.5 leading-tight gradient-text">
            QUAZI
          </h1>
          <p className="text-lg text-secondary font-medium tracking-wider uppercase mb-4 leading-normal">
            {t('tagline')}
          </p>
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-brand/20 border border-brand rounded-xl text-brand-light text-[0.6875rem] font-semibold uppercase tracking-wider">
            <Lock size={14} />
            <span>GSI Secure System</span>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex flex-col gap-3.5">
          {/* Email Input */}
          <div className="relative flex items-center">
            <Mail size={20} className="absolute left-5 text-tertiary pointer-events-none z-10" />
            <input
              type="email"
              placeholder={t('emailPlaceholder')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full py-4 pr-4 pl-[3.25rem] bg-surface-elevated border border-medium rounded-xl text-content text-lg font-normal transition-all duration-250 placeholder:text-tertiary focus:outline-none focus:border-brand focus:bg-surface-secondary focus:shadow-[0_0_0_3px_rgba(33,158,188,0.1)]"
              required
              autoComplete="email"
            />
          </div>

          {/* Password Input */}
          <div className="relative flex items-center">
            <Lock size={20} className="absolute left-5 text-tertiary pointer-events-none z-10" />
            <input
              type={showPassword ? "text" : "password"}
              placeholder={t('passwordPlaceholder')}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={handleKeyPress}
              onKeyUp={handleKeyPress}
              className="w-full py-4 pr-12 pl-[3.25rem] bg-surface-elevated border border-medium rounded-xl text-content text-lg font-normal transition-all duration-250 placeholder:text-tertiary focus:outline-none focus:border-brand focus:bg-surface-secondary focus:shadow-[0_0_0_3px_rgba(33,158,188,0.1)]"
              required
              autoComplete="current-password"
            />
            {capsLockOn && (
              <div className="absolute top-1/2 right-14 -translate-y-1/2 bg-status-error/10 border border-status-error rounded-lg px-2.5 py-1.5 text-xs text-status-error-light font-medium whitespace-nowrap z-10 animate-fade-in">
                Caps Lock
              </div>
            )}
            <button
              type="button"
              className="absolute right-5 text-tertiary cursor-pointer z-10 p-1 transition-colors duration-250 hover:text-primary"
              onClick={() => setShowPassword(!showPassword)}
              title={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>

          {/* Remember Me */}
          <div className="flex items-center gap-2.5 my-1">
            <input
              type="checkbox"
              id="rememberMe"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              className="w-[18px] h-[18px] cursor-pointer accent-brand"
            />
            <label htmlFor="rememberMe" className="text-[0.9375rem] text-secondary cursor-pointer select-none">
              {t('rememberMe')}
            </label>
          </div>

          {/* Error Message */}
          {error && (
            <div className="p-4 bg-status-error/10 border border-status-error rounded-xl text-status-error-light text-base font-medium text-center leading-normal animate-fade-in">
              {error}
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            className="relative flex items-center justify-center gap-3 w-full py-4 px-6 bg-gradient-to-br from-brand to-brand-hover border-none rounded-xl text-content-inverse text-lg font-semibold cursor-pointer uppercase tracking-widest shadow-glow mt-4 overflow-hidden transition-all duration-250 hover:translate-y-[-3px] hover:shadow-glow-strong active:translate-y-[-1px] disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:translate-y-0 group"
            disabled={loading}
          >
            {/* Shine effect */}
            <span className="absolute top-0 -left-full w-full h-full bg-gradient-to-r from-transparent via-white/30 to-transparent transition-[left] duration-500 group-hover:left-full" />
            {loading ? (
              <>
                <span className="w-5 h-5 border-2 border-content/30 border-t-content rounded-full animate-spin" />
                {t('authenticating')}
              </>
            ) : (
              t('signIn')
            )}
          </button>

          {/* Forgot Password */}
          <button
            type="button"
            className="bg-transparent border-none text-primary text-[0.9375rem] font-medium cursor-pointer py-2 mt-2 transition-all duration-250 hover:text-brand-hover hover:underline"
            onClick={onShowForgotPassword}
          >
            {t('forgotPassword')}
          </button>
        </form>

        {/* Footer */}
        <div className="mt-4 text-center pt-4 border-t border-subtle">
          <p className="text-[0.6875rem] text-secondary font-medium m-0">
            🇲🇽 A product of GSI Mexico
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginForm;
